import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../../../components/admin/PageHeader";
import { DataTable } from "../../../components/admin/DataTable";
import { FormModal } from "../../../components/admin/FormModal";
import { ConfirmDeleteModal } from "../../../components/admin/ConfirmDeleteModal";
import { NameDescriptionForm } from "../../../components/admin/NameDescriptionForm";
import { useCrudList } from "../../../hooks/useCrudList";
import { categoriesService } from "../services/categories";

function formatDate(iso, lang) {
  if (!iso) return "";
  const locale = lang?.startsWith("en") ? "en-GB" : "de-DE";
  return new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "short", day: "2-digit" });
}

export default function CategoriesPage() {
  const { t, i18n } = useTranslation();
  const crud = useCrudList({
    service: categoriesService,
    defaultSort: { key: "name", dir: "asc" },
    sessionKey: "categories.pageSize",
  });

  const [formValue, setFormValue] = useState({ name: "", description: "" });

  // Pre-fill form on edit; reset on create.
  useEffect(() => {
    if (!crud.modalOpen) return;
    if (crud.editingRow) {
      setFormValue({
        name: crud.editingRow.name || "",
        description: crud.editingRow.description || "",
      });
    } else {
      setFormValue({ name: "", description: "" });
    }
  }, [crud.modalOpen, crud.editingRow]);

  const columns = [
    { key: "name", labelKey: "admin.categories.col_name", sortable: true, className: "w-1/3" },
    {
      key: "description",
      labelKey: "admin.categories.col_description",
      render: (r) => (
        <span className="line-clamp-2 text-text-secondary">{r.description || "—"}</span>
      ),
    },
    {
      key: "created_at",
      labelKey: "admin.common.created_at",
      sortable: true,
      className: "w-40 whitespace-nowrap",
      render: (r) => (
        <span className="font-mono text-xs text-text-secondary">
          {formatDate(r.created_at, i18n.language)}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6" data-testid="categories-page">
        <PageHeader
          title={t("admin.categories.title")}
          subtitle={t("admin.categories.subtitle")}
          actionLabel={t("admin.common.add_new")}
          onAction={crud.openCreate}
          actionTestId="categories-add-new"
        />
        <DataTable
          columns={columns}
          items={crud.items}
          loading={crud.loading}
          total={crud.total}
          page={crud.page}
          pageSize={crud.pageSize}
          onPageChange={crud.setPage}
          onPageSizeChange={crud.setPageSize}
          sortKey={crud.sortKey}
          sortDir={crud.sortDir}
          onSortChange={crud.changeSort}
          searchValue={crud.searchValue}
          onSearchChange={crud.setSearchValue}
          onEdit={crud.openEdit}
          onDelete={crud.askDelete}
          entityTestId="categories"
        />
      </div>

      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        mode={crud.editingRow ? "edit" : "create"}
        entityLabel={t("admin.categories.entity_singular")}
        saving={crud.saving}
        onSubmit={() =>
          crud.save({
            name: formValue.name.trim(),
            description: formValue.description?.trim() || null,
          })
        }
        testId="categories-form"
        submitTestId="categories-form-submit"
        cancelTestId="categories-form-cancel"
      >
        <NameDescriptionForm
          value={formValue}
          onChange={setFormValue}
          nameFieldLabelKey="admin.categories.field_name"
          descriptionFieldLabelKey="admin.categories.field_description"
          testIdPrefix="categories-form"
        />
      </FormModal>

      <ConfirmDeleteModal
        open={Boolean(crud.deleteRow)}
        onClose={crud.cancelDelete}
        onConfirm={crud.confirmDelete}
        name={crud.deleteRow?.name || ""}
        busy={crud.deleting}
        testId="categories-delete"
      />
    </>
  );
}
